import {
    Button,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Text,
    VStack,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import PartBayButton from '../PartBayButton';
import SearchableSingleSelect from '../SearchableSingleSelect';
import { LocationsPicker } from './UsersInlineCells';
import type { LocationEntry } from '../../hooks/useGetLocations';
import type { Role } from '../../hooks/useValidateSession';

/** Fields the bulk bar can set across a selection. */
export type UsersBulkField = 'status' | 'position' | 'locations' | 'role';

export interface UsersBulkValue {
    active?: boolean;
    position?: Role;
    locationId?: Array<string> | 'all';
    /** Admin flag; `false` is a real value here, meaning "make them members". */
    admin?: boolean;
}

export interface UsersBulkEditModalProps {
    field: UsersBulkField | null;
    selectedCount: number;
    positionOptions: Array<{ value: Role; label: string }>;
    locations: LocationEntry[];
    isSaving: boolean;
    onApply: (value: UsersBulkValue) => void;
    onClose: () => void;
}

const FIELD_COPY: Record<UsersBulkField, { title: string; label: string }> = {
    status: { title: 'Change status', label: 'Status' },
    position: { title: 'Change position', label: 'Position' },
    locations: { title: 'Change locations', label: 'Locations' },
    role: { title: 'Change role', label: 'Role' },
};

const STATUS_OPTIONS = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
];

const ROLE_OPTIONS = [
    { value: 'member', label: 'Member' },
    { value: 'admin', label: 'Admin' },
];

/**
 * One value, applied to every selected user. The bulk bar decides which field
 * is being changed; this only collects the new value and hands it back as an
 * edit payload fragment.
 */
const UsersBulkEditModal = ({
    field,
    selectedCount,
    positionOptions,
    locations,
    isSaving,
    onApply,
    onClose,
}: UsersBulkEditModalProps) => {
    const [status, setStatus] = useState('active');
    const [position, setPosition] = useState<Role | ''>('');
    const [locationId, setLocationId] = useState<Array<string> | 'all'>('all');
    const [role, setRole] = useState('member');

    // Reset between openings so a previous pick never carries over.
    useEffect(() => {
        if (!field) return;
        setStatus('active');
        setPosition('');
        setLocationId('all');
        setRole('member');
    }, [field]);

    if (!field) return null;

    const noLocationsPicked = locationId !== 'all' && locationId.length === 0;
    const canApply =
        (field === 'status' && status !== '') ||
        (field === 'position' && position !== '') ||
        (field === 'locations' && !noLocationsPicked) ||
        field === 'role';

    const handleApply = () => {
        if (field === 'status') onApply({ active: status === 'active' });
        else if (field === 'position') onApply({ position: position as Role });
        else if (field === 'role') onApply({ admin: role === 'admin' });
        else onApply({ locationId });
    };

    return (
        <Modal isOpen onClose={onClose} isCentered size="md">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader fontSize="md" fontWeight="semibold">
                    {FIELD_COPY[field].title}
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody pb={2}>
                    <VStack align="stretch" spacing={4}>
                        <Text fontSize="sm" color="gray.500">
                            Applies to {selectedCount} selected user{selectedCount === 1 ? '' : 's'}.
                        </Text>

                        {field === 'status' && (
                            <SearchableSingleSelect
                                width="100%"
                                options={STATUS_OPTIONS}
                                value={status}
                                onChange={setStatus}
                            />
                        )}

                        {field === 'position' && (
                            <SearchableSingleSelect
                                width="100%"
                                options={positionOptions}
                                value={position}
                                placeholder="Select position"
                                onChange={(next) => setPosition(next as Role)}
                            />
                        )}

                        {field === 'role' && (
                            <SearchableSingleSelect
                                width="100%"
                                options={ROLE_OPTIONS}
                                value={role}
                                onChange={setRole}
                            />
                        )}

                        {field === 'locations' && (
                            <LocationsPicker
                                value={locationId}
                                locations={locations}
                                onChange={setLocationId}
                                maxH="260px"
                            />
                        )}

                        {field === 'locations' && noLocationsPicked && (
                            <Text fontSize="xs" color="red.500">
                                Pick at least one location.
                            </Text>
                        )}
                    </VStack>
                </ModalBody>
                <ModalFooter gap={2}>
                    <Button
                        size="sm"
                        variant="outlineGray"
                        onClick={onClose}
                        isDisabled={isSaving}
                    >
                        Cancel
                    </Button>
                    <PartBayButton
                        size="sm"
                        variant="solid"
                        colorScheme="partbay-blue"
                        isDisabled={!canApply}
                        isLoading={isSaving}
                        loadingText="Applying"
                        onClick={handleApply}
                        text="Apply"
                    />
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default UsersBulkEditModal;
